import { supabase } from './supabase';

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  return Notification.requestPermission();
}

export async function showSystemNotification(options: {
  title: string;
  body: string;
  tag?: string;
  url?: string;
  entityType?: string;
  entityId?: string;
  persistent?: boolean;
}): Promise<boolean> {
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return false;
  const registration = await registerServiceWorker();
  if (!registration) return false;
  await registration.showNotification(options.title, {
    body: options.body,
    icon: '/icons/icon-192.png',
    badge: '/icons/badge-96.png',
    tag: options.tag,
    requireInteraction: Boolean(options.persistent),
    data: { url: options.url || '/', entityType: options.entityType, entityId: options.entityId },
  });
  return true;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(character => character.charCodeAt(0)));
}

export async function subscribeToPush(): Promise<{ supported: boolean; subscribed: boolean; message: string }> {
  const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, subscribed: false, message: 'Web Push is not supported on this browser.' };
  }
  if (!publicKey) return { supported: true, subscribed: false, message: 'VAPID public key has not been configured yet.' };
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return { supported: true, subscribed: false, message: 'Notification permission was not granted.' };
  const registration = await registerServiceWorker();
  if (!registration) return { supported: false, subscribed: false, message: 'Service worker registration failed.' };
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
  });
  const { data: authData } = await supabase.auth.getUser();
  const user = authData.user;
  if (!user) throw new Error('Sign in before enabling push notifications.');
  const json = subscription.toJSON();
  const { error } = await supabase.from('mdmc_push_subscriptions').upsert({
    user_id: user.id,
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent,
    device_name: 'Matter Dashboard iPhone',
    enabled: true,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,endpoint' });
  if (error) throw error;
  return { supported: true, subscribed: true, message: 'iPhone Web Push is enabled for this device.' };
}

export async function testRemotePush(): Promise<{ sent: boolean; message: string }> {
  const endpoint = import.meta.env.VITE_PUSH_FUNCTION_URL as string | undefined;
  if (!endpoint) return { sent: false, message: 'Remote push test endpoint has not been configured yet.' };
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in before testing Web Push.');
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      title: 'Matter Dashboard',
      body: 'Your iPhone Web Push notification is working.',
      url: '/?page=activity',
      tag: `mdm-test-${Date.now()}`,
    }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Push test failed (${response.status}).`);
  return { sent: true, message: `Push sent to ${body.sent || 0} device${body.sent === 1 ? '' : 's'}.` };
}

export async function playTabletopBell(): Promise<void> {
  const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return;
  const context = new AudioContextClass();
  const now = context.currentTime;
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.22, now + 0.008);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 1.25);
  master.connect(context.destination);
  [880, 1320, 1760].forEach((frequency, index) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = index === 0 ? 'sine' : 'triangle';
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.985, now + 1.1);
    gain.gain.setValueAtTime(0.45 / (index + 1), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    oscillator.connect(gain).connect(master);
    oscillator.start(now);
    oscillator.stop(now + 1.3);
  });
  window.setTimeout(() => void context.close(), 1500);
}
